import React, { useState, useEffect, useRef } from 'react';
import { supabaseClient } from '../../services/supabase';

// 画像リサイズ関数
const resizeImage = (file, maxWidth = 1200, maxHeight = 800, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// デフォルトの店舗写真設定
const defaultPhotos = [
  { photo_type: 'counter', title: 'カウンター', image_url: '/images/counter.jpg' },
  { photo_type: 'koagari', title: '小上がり', image_url: '/images/zashiki.jpg' }
];

function StorePhotosManagement() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    photo_type: 'counter',
    title: '',
    is_visible: true,
    imageFile: null,
    imagePreview: null
  });
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    loadPhotos();
  }, []);

  // モーダルが開いたときに自動スクロール
  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      setTimeout(() => {
        modalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
    }
  }, [isModalOpen]);

  const loadPhotos = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseClient
        .from('store_photos')
        .select('*')
        .order('photo_type', { ascending: true });

      if (error) throw error;
      
      // データベースから取得したデータをマップに変換
      const dbPhotosMap = {};
      if (data && data.length > 0) {
        data.forEach(photo => {
          dbPhotosMap[photo.photo_type] = photo;
        });
      }
      
      // デフォルト写真とデータベースのデータをマージ
      // カウンターと小上がりの両方が常に表示されるようにする
      const mergedPhotos = defaultPhotos.map((defaultPhoto, i) => {
        const dbPhoto = dbPhotosMap[defaultPhoto.photo_type];
        if (dbPhoto) {
          // データベースにデータがある場合はそれを使用
          return dbPhoto;
        } else {
          // データベースにデータがない場合はデフォルト値を使用
          return { ...defaultPhoto, id: `default-${i}`, is_default: true };
        }
      });
      
      setPhotos(mergedPhotos);
    } catch (error) {
      console.error('読み込みエラー:', error);
      // テーブルがない場合もデフォルト値を表示
      setPhotos(defaultPhotos.map((p, i) => ({ ...p, id: `default-${i}`, is_default: true })));
    } finally {
      setLoading(false);
    }
  };

  const openModal = (photo = null) => {
    if (photo) {
      setEditingPhoto(photo);
      setFormData({
        photo_type: photo.photo_type || 'counter',
        title: photo.title || '',
        is_visible: photo.is_visible !== false,
        imageFile: null,
        imagePreview: photo.image_url
      });
    } else {
      setEditingPhoto(null);
      setFormData({
        photo_type: 'counter',
        title: '',
        is_visible: true,
        imageFile: null,
        imagePreview: null
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPhoto(null);
    setFormData({
      photo_type: 'counter',
      title: '',
      is_visible: true,
      imageFile: null,
      imagePreview: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください。');
      return;
    }

    // ファイルサイズチェック（10MB以下）
    if (file.size > 10 * 1024 * 1024) {
      alert('ファイルサイズは10MB以下にしてください。');
      return;
    }

    // プレビュー表示
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('タイトルを入力してください。');
      return;
    }
    if (!editingPhoto?.image_url && !formData.imageFile) {
      alert('画像を選択してください。');
      return;
    }

    setUploading(true);
    try {
      let imageUrl = editingPhoto?.image_url;

      if (formData.imageFile) {
        // 画像をリサイズ
        const resizedBlob = await resizeImage(formData.imageFile);
        const fileName = `store_${formData.photo_type}_${Date.now()}.jpg`;
        const { error: uploadError } = await supabaseClient.storage
          .from('store-photos')
          .upload(fileName, resizedBlob, {
            contentType: 'image/jpeg',
            upsert: true
          });

        if (uploadError) {
          // RLSポリシーエラーの場合、詳細なメッセージを表示
          if (uploadError.message && uploadError.message.includes('row-level security')) {
            throw new Error('ストレージバケット「store-photos」のRLSポリシーが設定されていません。\n\nSupabaseダッシュボードで以下を設定してください：\n1. Storage > store-photos バケットを作成（Public bucket: ON）\n2. SQL EditorでRLSポリシーを設定\n\n詳細はSUPABASE_SETUP.mdを参照してください。');
          }
          throw uploadError;
        }

        const { data: urlData } = supabaseClient.storage
          .from('store-photos')
          .getPublicUrl(fileName);

        imageUrl = urlData.publicUrl;

        // 古い画像を削除（Supabaseにアップロードした画像のみ）
        if (editingPhoto?.image_url && editingPhoto.image_url.includes('supabase')) {
          const oldFileName = editingPhoto.image_url.split('/').pop();
          const { error: deleteError } = await supabaseClient.storage
            .from('store-photos')
            .remove([oldFileName]);
          
          // 削除エラーは警告のみ（新規アップロードは成功しているため）
          if (deleteError) {
            console.warn('古い画像の削除に失敗しました:', deleteError);
          }
        }
      }

      const photoData = {
        photo_type: formData.photo_type,
        title: formData.title.trim(),
        image_url: imageUrl,
        is_visible: formData.is_visible,
        updated_at: new Date().toISOString()
      };

      if (editingPhoto && !editingPhoto.is_default) {
        // 既存レコードを更新
        const { error } = await supabaseClient
          .from('store_photos')
          .update(photoData)
          .eq('id', editingPhoto.id);

        if (error) throw error;
        alert('更新しました。');
      } else {
        // 新規作成（デフォルトからの置き換えも含む）
        // 同じphoto_typeのレコードがあれば削除
        await supabaseClient
          .from('store_photos')
          .delete()
          .eq('photo_type', formData.photo_type);
        
        const { error } = await supabaseClient
          .from('store_photos')
          .insert(photoData);

        if (error) throw error;
        alert('保存しました。');
      }

      closeModal();
      loadPhotos();
    } catch (error) {
      console.error('保存エラー:', error);
      alert('保存に失敗しました。\n' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleToggleVisibility = async (photo) => {
    if (photo.is_default) {
      alert('この写真を編集するには、まず「編集」から画像をアップロードしてください。');
      return;
    }
    
    try {
      const { error } = await supabaseClient
        .from('store_photos')
        .update({ is_visible: !photo.is_visible })
        .eq('id', photo.id);

      if (error) throw error;
      loadPhotos();
    } catch (error) {
      console.error('更新エラー:', error);
      alert('更新に失敗しました。');
    }
  };

  const getPhotoTypeLabel = (type) => {
    switch (type) {
      case 'counter': return '🪑 カウンター';
      case 'koagari': return '🏠 小上がり';
      default: return type;
    }
  };

  return (
    <section className="admin-section">
      <div className="container">
        <h2>店舗写真管理</h2>

        <p style={{ color: '#888', marginBottom: '20px' }}>
          店舗情報セクションに表示される「カウンター」と「小上がり」の写真を管理します。
        </p>

        {loading ? (
          <p>読み込み中...</p>
        ) : (
          <div className="store-photos-grid">
            {photos.map((photo) => (
              <div 
                key={photo.id} 
                className={`store-photo-card ${!photo.is_visible ? 'hidden-item' : ''}`}
              >
                <div className="store-photo-type">
                  {getPhotoTypeLabel(photo.photo_type)}
                </div>
                <div className="store-photo-image">
                  <img src={photo.image_url} alt={photo.title} />
                  {!photo.is_visible && (
                    <div className="hidden-badge">非表示</div>
                  )}
                  {photo.is_default && (
                    <div className="default-badge">デフォルト画像</div>
                  )}
                </div>
                <div className="store-photo-info">
                  <p className="store-photo-title">{photo.title}</p>
                </div>
                <div className="store-photo-actions">
                  <button 
                    className={`btn-small ${photo.is_visible ? 'btn-toggle' : 'btn-show'}`}
                    onClick={() => handleToggleVisibility(photo)}
                    disabled={photo.is_default}
                  >
                    {photo.is_visible ? '非表示' : '表示'}
                  </button>
                  <button 
                    className="btn-small btn-edit"
                    onClick={() => openModal(photo)}
                  >
                    編集
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 編集モーダル */}
        {isModalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" ref={modalRef} onClick={(e) => e.stopPropagation()}>
              <h3>店舗写真編集</h3>
              
              <div className="form-group">
                <label>写真タイプ</label>
                <select
                  value={formData.photo_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, photo_type: e.target.value }))}
                  disabled={editingPhoto !== null}
                >
                  <option value="counter">カウンター</option>
                  <option value="koagari">小上がり</option>
                </select>
              </div>

              <div className="form-group">
                <label>タイトル *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="例: カウンター"
                />
              </div>

              <div className="form-group">
                <label>画像 *</label>
                <div className="image-upload-area">
                  {formData.imagePreview ? (
                    <div className="image-preview">
                      <img src={formData.imagePreview} alt="プレビュー" />
                      <button 
                        type="button" 
                        className="btn-change-image"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        画像を変更
                      </button>
                    </div>
                  ) : (
                    <div 
                      className="image-dropzone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <p>クリックして画像を選択</p>
                      <p className="hint">JPEG, PNG, WebP（最大10MB）</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </div>
                <p className="form-hint">※アップロード時に自動でリサイズされます（最大1200×800px）</p>
              </div>

              <div className="form-group">
                <label>表示設定</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_visible}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_visible: e.target.checked }))}
                  />
                  サイトに表示する
                </label>
              </div>

              <div className="modal-actions">
                <button 
                  className="btn-secondary" 
                  onClick={closeModal}
                  disabled={uploading}
                >
                  キャンセル
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSave}
                  disabled={uploading}
                >
                  {uploading ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .store-photos-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
        }

        .store-photo-card {
          background: #1a1a1a;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #333;
        }

        .store-photo-card.hidden-item {
          opacity: 0.6;
        }

        .store-photo-type {
          background: #2a2a2a;
          color: #ffffff;
          padding: 10px 15px;
          font-weight: bold;
          font-size: 1rem;
        }

        .store-photo-image {
          position: relative;
          width: 100%;
          height: 200px;
          overflow: hidden;
        }

        .store-photo-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .hidden-badge {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(100, 100, 100, 0.9);
          color: white;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: bold;
        }

        .default-badge {
          position: absolute;
          bottom: 10px;
          right: 10px;
          background: rgba(255, 165, 0, 0.9);
          color: white;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }

        .store-photo-info {
          padding: 15px;
        }

        .store-photo-title {
          color: #ffffff;
          font-weight: bold;
          margin: 0;
        }

        .store-photo-actions {
          display: flex;
          gap: 8px;
          padding: 15px;
          border-top: 1px solid #333;
          flex-wrap: wrap;
        }

        .btn-small {
          padding: 6px 12px;
          font-size: 0.85rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          min-width: 60px;
        }

        .btn-small:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-toggle {
          background: #555;
          color: white;
        }

        .btn-show {
          background: #28a745;
          color: white;
        }

        .btn-edit {
          background: #2196F3;
          color: white;
        }

        .image-upload-area {
          margin-top: 10px;
        }

        .image-preview {
          position: relative;
          max-width: 300px;
        }

        .image-preview img {
          width: 100%;
          border-radius: 8px;
        }

        .btn-change-image {
          display: block;
          margin-top: 10px;
          padding: 8px 16px;
          background: #2196F3;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .btn-change-image:hover {
          background: #1976D2;
        }

        .image-dropzone {
          border: 2px dashed #444;
          border-radius: 8px;
          padding: 40px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.3s ease;
        }

        .image-dropzone:hover {
          border-color: #666;
        }

        .image-dropzone p {
          color: #888;
          margin: 5px 0;
        }

        .image-dropzone .hint {
          font-size: 0.85rem;
          color: #666;
        }

        .form-hint {
          font-size: 0.8rem;
          color: #888;
          margin-top: 5px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #ffffff;
          cursor: pointer;
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
        }
      `}</style>
    </section>
  );
}

export default StorePhotosManagement;

